import { useState } from 'react';
import { Badge, Button, Input, Select } from '../../components/ui';
import { FilterMenu, FilterMenuItem } from './FilterMenu';
import { MENU_LABELS, menuCounts } from './leadFilterMenus';
import type { MenuKey } from './leadFilterMenus';
import { POSITION_ORDER } from './leadPosition';
import {
  SAVED_VIEWS,
  EMPTY_FILTER,
  LEAD_TABS,
  LOOP_FILTERS,
  activeFilterTokens,
  activeLeadTab,
  activeViewKey,
  categoryIdByName,
  isFiltering,
  withLeadTab,
} from './leadFilters';
import { offerable, originLabel } from './leadFields';
import { hasChoiceWithin, originGroups, valueLabel } from './leadOriginFilter';
import type { LeadPosition } from './leadPosition';
import type { LeadFilter, LeadTab } from './leadFilters';
import type { EvidenceGroup } from './leadEvidence';
import type { LeadCategory, LeadClosureReason, LeadSource } from './leadFields';
import type { LeadStage } from './types';

/**
 * The Leads list's filter bar, and the saved views above it.
 *
 * Its own file because TrackerPage.tsx is 4,993 lines holding nine tabs, and
 * the restructure names that as the first obstacle. Everything new goes
 * beside it rather than into it.
 *
 * ── Saved views are the point ──────────────────────────────────────────────
 *
 * The restructure exists because Alex cannot answer "which Corporate Gifting
 * leads came in recently, who contacted them, and what happens next". The
 * first half of that is one click — and a set of filters nobody can find is
 * the same as no filters, which is why the views sit ABOVE the controls
 * rather than behind a menu.
 *
 * Choosing a view fills the controls in rather than hiding them, so the next
 * thing somebody does is adjust it, not start again.
 *
 * ── Six dropdowns, not forty-two chips ─────────────────────────────────────
 *
 * Kyu, 31 Aug, with the panel open on screen: "this is what I mean. it's too
 * noisy", and before that "Maybe change them to Dropdown." Counted on that
 * screenshot: 35 chips inside the disclosure and 7 more in the Status row
 * above it. Measured against the real stylesheet, the bar stood 404px tall; as
 * six dropdowns it stands 143px.
 *
 * The chips had been doing two jobs: control surface AND the only display of
 * what was active. That is why the summary row was built first, and why it is
 * what makes this pass safe — with the display job handed to that row entirely,
 * the control can be as small as we like.
 *
 * Two mechanisms now carry every folded filter, and this pass is worse than
 * not doing it if either is dropped:
 *   1. a count on the menu button whenever that axis is narrowing
 *      (`menuCounts`, in leadFilterMenus.ts, typed so a new LeadFilter field
 *      cannot be forgotten)
 *   2. the active-filter summary row, which names every live filter whether or
 *      not its menu is open
 *
 * The `Filters` disclosure is gone. Nothing is left to hide behind it, and it
 * had a cost worth naming: it expanded IN PLACE, so touching any low-frequency
 * filter pushed the leads table 240px down the screen.
 *
 * What did NOT move into a menu: search, the cohort sub-tabs, and the evidence
 * row inside Needs Review. The first two are navigation; the evidence chips
 * carry counts and a claim you are agreeing to, which is a thing to read
 * rather than a filter to set.
 */

/**
 * The five a human sets plus the two the deals decide. Won and Lost are not
 * rows in `lead_stages`, so unlike the other five they cannot be renamed by an
 * admin and are named here.
 */
const POSITION_LABELS: Record<LeadPosition, string> = {
  new: 'New',
  contacted: 'Contacted',
  followup: 'Follow Up',
  engaged: 'Engaged',
  nurture: 'Nurture',
  converted: 'Converted to Deal',
  won: 'Won',
  lost: 'Lost',
  closed: 'Closed',
};

/**
 * The three windows worth a chip, in days.
 *
 * 365 rather than "this year" is the whole argument of the group — see the
 * comment at the chips. 30 and 90 are the two shorter cuts somebody actually
 * asks for: what did we lose this month, and what did we lose this quarter.
 */
const CLOSED_WINDOWS: { days: number; label: string }[] = [
  { days: 30, label: 'Last 30 days' },
  { days: 90, label: 'Last 3 months' },
  { days: 365, label: 'Last 12 months' },
];

export function LeadFilterBar({
  filter,
  onChange,
  stages,
  categories,
  matchCount,
  totalCount,
  tabCounts,
  roundsView,
  onToggleRounds,
  origins = [],
  evidenceGroups = [],
  closureReasons = [],
}: {
  filter: LeadFilter;
  onChange: (next: LeadFilter) => void;
  stages: LeadStage[];
  categories: LeadCategory[];
  matchCount: number;
  totalCount: number;
  tabCounts: Record<LeadTab, number>;
  /**
   * The Rounds work-queue view — drawn by the page, toggled from this bar.
   * Only the Leads tab draws it, so only the Leads tab passes these; the
   * Pipeline shares this bar and used to show a Rounds button that redrew
   * nothing on the screen it sat on (it silently flipped the Leads tab
   * instead). No handler, no button.
   */
  roundsView?: boolean | undefined;
  onToggleRounds?: (() => void) | undefined;
  /** The Lead Origin vocabulary. Empty until it loads; the group row hides. */
  origins?: LeadSource[];
  /** Evidence groups inside Needs Review. Empty on the other tabs. */
  evidenceGroups?: EvidenceGroup[];
  /**
   * The closing-reason vocabulary. Empty until it loads; the group hides.
   *
   * Passed in rather than fetched here for the same reason `origins` is: the
   * page already holds the query, and a second subscription would give the
   * chips and the leads two different ideas of what the vocabulary is.
   */
  closureReasons?: LeadClosureReason[];
}) {
  const byName = categoryIdByName(categories);
  const groupsOfOrigin = originGroups(origins);
  const chosenOriginGroup = groupsOfOrigin.find((group) => group.name === filter.originGroup);
  // Retired values keep rendering on the leads that carry them, but a picker
  // must not offer one — that is the whole reason deactivating exists instead
  // of deleting.
  const liveCategories = offerable(categories);
  const liveReasons = offerable(closureReasons);
  const knownKeys = new Set(stages.map((stage) => (stage.key ?? '').toLowerCase()));

  const counts = menuCounts(filter);
  // One menu open at a time. Two open menus overlapping each other on a bar
  // this dense is unreadable, and nobody sets two axes simultaneously anyway.
  const [openMenu, setOpenMenu] = useState<MenuKey | null>(null);
  const menuProps = (key: MenuKey) => ({
    label: MENU_LABELS[key],
    count: counts[key],
    open: openMenu === key,
    onOpenChange: (next: boolean) => {
      setOpenMenu(next ? key : null);
    },
  });

  const currentTab = activeLeadTab(filter);
  const selectedEvidence = evidenceGroups.find((group) => group.key === filter.evidenceGroup);
  const currentView = activeViewKey(filter, byName);
  const tokens = activeFilterTokens(filter, {
    status: (key) => POSITION_LABELS[key],
    category: (id) => categories.find((entry) => entry.id === id)?.name ?? null,
    // The WHOLE vocabulary, not `liveReasons`. A reason somebody deactivated
    // still sits on the leads closed for it, so a filter on it must still be
    // able to say its name in the summary row — the picker is the only place
    // deactivation should hide a value.
    closureReason: (id) => closureReasons.find((entry) => entry.id === id)?.name ?? null,
    // The platform if one was picked, otherwise the group. `originLabel`
    // prints "Advertising › LinkedIn Ads", which is what the picker above
    // says, so the summary row and the control cannot describe the same
    // filter with different words.
    origin: (group, id) => {
      if (id === null) {
        return group;
      }
      const value = origins.find((entry) => entry.id === id);
      return value === undefined ? group : originLabel(value);
    },
    loop: (value) => LOOP_FILTERS.find((entry) => entry.value === value)?.label ?? value,
    evidence: (key) => evidenceGroups.find((entry) => entry.key === key)?.label ?? null,
  });

  const toggle = <T,>(list: T[], value: T): T[] =>
    list.includes(value) ? list.filter((entry) => entry !== value) : [...list, value];

  return (
    <div className="lead-filter-bar">
      {/* Navigation, not a filter — so it sits above the saved views, which
          are questions asked WITHIN a cohort. Every lead is in exactly one of
          these four, which is the whole reason Needs Review is a tab: on the
          measured data, Inbound and Outbound alone would account for 119 of
          688 leads and silently drop the other 569. */}
      <div className="lead-subtabs" role="tablist" aria-label="Lead cohorts">
        {LEAD_TABS.map((tab) => {
          const on = currentTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={on}
              className={`lead-subtab${on ? ' lead-subtab--on' : ''}`}
              title={tab.description}
              onClick={() => onChange(withLeadTab(filter, tab.key))}
            >
              {tab.label}
              {/* The count rides on the tab because the size of the Needs
                  Review pile is the fact somebody has to see without going
                  looking for it — and it shrinks as the queue is worked. */}
              <span className="lead-subtab-count">{tabCounts[tab.key]}</span>
            </button>
          );
        })}
      </div>

      {/* Evidence, and only inside Needs Review.

          569 leads with no Lead Source is not a queue anybody works down one
          row at a time. Grouped by what the Hub already knows — on an outbound
          cadence, on a named campaign list, part of an import somebody
          remembers — it becomes four or five decisions instead of 569.

          The claim under the chips is the point of the whole row: you are
          confirming a sentence about these leads, not approving a number. */}
      {currentTab !== 'needs-review' || evidenceGroups.length === 0 ? null : (
        <div className="lead-evidence">
          <FilterGroup label="Evidence">
            {evidenceGroups.map((group) => {
              const on = filter.evidenceGroup === group.key;
              return (
                <Chip
                  key={group.key}
                  on={on}
                  onClick={() => onChange({ ...filter, evidenceGroup: on ? null : group.key })}
                >
                  {group.label}
                  <span className="lead-evidence-count">{group.leads}</span>
                </Chip>
              );
            })}
          </FilterGroup>
          {selectedEvidence === undefined ? null : (
            <p className="tracker-field-note">
              {selectedEvidence.claim}
              {selectedEvidence.withSends === 0
                ? ''
                : ` ${String(selectedEvidence.withSends)} of ${String(selectedEvidence.leads)} have a touch recorded.`}
              {selectedEvidence.proposes === null
                ? ' No Lead Source is proposed for these — the evidence does not settle it.'
                : ` This evidence supports ${selectedEvidence.proposes === 'inbound' ? 'Inbound' : 'Outbound'}.`}
            </p>
          )}
        </div>
      )}

      <div className="lead-filter-controls">
        <div className="lead-filter-search">
          <Input
            label="Search leads"
            placeholder="Name, email, company…"
            value={filter.query}
            onChange={(event) => onChange({ ...filter, query: event.target.value })}
          />
        </div>

        {/* Status is a menu like the rest, and it is the one that was arguable.
            It is the axis people re-filter on repeatedly in a session, so the
            extra click genuinely costs something here and nowhere else. It
            goes in anyway because Status is the row Kyu circled: seven chips
            permanently open under the search box, explained by nothing. A row
            of six identical buttons can be read; a row plus a wall cannot. */}
        <FilterMenu {...menuProps('status')}>
          {/* Won and Lost are offered whatever the stage vocabulary says,
              because they are read off the deals rather than looked up in
              lead_stages — the knownKeys guard exists to stop offering a
              status the admin removed, and it cannot speak for these two. */}
          {POSITION_ORDER.filter(
            (key) => knownKeys.has(key) || key === 'won' || key === 'lost',
          ).map((key) => (
            <FilterMenuItem
              key={key}
              label={POSITION_LABELS[key]}
              on={filter.statusKeys.includes(key)}
              kind="many"
              onClick={() => {
                onChange({ ...filter, statusKeys: toggle(filter.statusKeys, key) });
              }}
            />
          ))}
        </FilterMenu>

        {liveCategories.length === 0 ? null : (
          <FilterMenu {...menuProps('category')}>
            {/* Retired values keep rendering on the leads that carry them, but
                a picker must not offer one — the whole reason deactivating
                exists instead of deleting. */}
            {liveCategories.map((category) => (
              <FilterMenuItem
                key={category.id}
                label={category.name}
                on={filter.categoryIds.includes(category.id)}
                kind="many"
                onClick={() => {
                  onChange({ ...filter, categoryIds: toggle(filter.categoryIds, category.id) });
                }}
              />
            ))}
          </FilterMenu>
        )}

        {/* ── Lead Origin, still in two steps ────────────────────────────────
            Kyu spotted this missing in Drop 23: the vocabulary and the drawer
            field shipped with no way to filter BY a value, so "show me the
            Instagram leads" was impossible.

            THE GROUP ALONE ALREADY FILTERS and the platform NARROWS. "How many
            came from social" is the common question and it costs one click; a
            required second step would tax it for no gain. The platform picker
            appears INSIDE this menu now rather than materialising elsewhere in
            the bar — a control that turns up in a different place than the one
            you clicked is how the old panel earned "what the hell am I looking
            at".

            SINGLE-SELECT. Two groups selected leaves the platform picker with
            no honest subject. */}
        {groupsOfOrigin.length === 0 ? null : (
          <FilterMenu {...menuProps('origin')}>
            {groupsOfOrigin.map((group) => (
              <FilterMenuItem
                key={group.name}
                label={group.name}
                on={filter.originGroup === group.name}
                kind="one"
                onClick={() => {
                  onChange({
                    ...filter,
                    originGroup: filter.originGroup === group.name ? null : group.name,
                    // Always cleared with the group. Keeping the previous
                    // group's platform would filter to a value the new group
                    // does not contain, and the list would go empty with both
                    // controls looking right.
                    originId: null,
                  });
                }}
              />
            ))}
            {chosenOriginGroup === undefined || !hasChoiceWithin(chosenOriginGroup) ? null : (
              <div className="lead-filter-menu-sub">
                <Select
                  label={`Which ${chosenOriginGroup.name.toLowerCase()}?`}
                  value={filter.originId ?? ''}
                  onChange={(event) => {
                    onChange({
                      ...filter,
                      originId: event.target.value === '' ? null : event.target.value,
                    });
                  }}
                  options={[
                    { value: '', label: `All ${chosenOriginGroup.name.toLowerCase()}` },
                    ...chosenOriginGroup.values.map((value) => ({
                      value: String(value.id),
                      label: valueLabel(chosenOriginGroup, value),
                    })),
                  ]}
                />
              </div>
            )}
          </FilterMenu>
        )}

        {/* SINGLE-SELECT, unlike most of these, and the odd one out on purpose.
            A status or a category narrows a list somebody is READING; these
            pick the queue somebody is going to WORK, and "Due today or never in
            a round" is not a morning's work, it is two. */}
        <FilterMenu {...menuProps('loop')}>
          {LOOP_FILTERS.map((entry) => (
            <FilterMenuItem
              key={entry.value}
              label={entry.label}
              on={filter.loop === entry.value}
              kind="one"
              title={entry.hint}
              onClick={() => {
                onChange({ ...filter, loop: filter.loop === entry.value ? null : entry.value });
              }}
            />
          ))}
          <p className="lead-filter-menu-note">
            Pick one. These are queues to work, not conditions to stack.
          </p>
        </FilterMenu>

        {/* ── Why it ended, and when, in ONE menu ────────────────────────────
            Kyu's question, and the only reason this exists: "who are the people
            that said no this year? ... so we know who to go back to when the
            time comes."

            "Closed because" and "Closed on" were two groups sitting apart in
            the old panel. They only ever apply to closed leads, and two
            controls for one situation is how you get one set and the other
            forgotten. Reasons are tick-many — "not right now OR no budget" is
            one re-engagement list. The window is pick-one.

            Reasons are read from `lead_closure_reasons`, never from a list
            written here: seven reasons were five the morning before Drop 37. */}
        <FilterMenu {...menuProps('closed')}>
          {liveReasons.length === 0 ? null : (
            <>
              <span className="lead-filter-menu-label">Because</span>
              {liveReasons.map((reason) => (
                <FilterMenuItem
                  key={reason.id}
                  label={reason.name}
                  on={filter.closedReasonIds.includes(reason.id)}
                  kind="many"
                  onClick={() => {
                    onChange({
                      ...filter,
                      closedReasonIds: toggle(filter.closedReasonIds, reason.id),
                    });
                  }}
                />
              ))}
            </>
          )}
          {/* Rolling windows rather than a calendar year. This is a
              re-engagement list: in August "this year" is eight months that
              stop dead at 1 January, and somebody who said not right now last
              November is exactly the one to call. */}
          <div className="lead-filter-menu-sub">
            <span className="lead-filter-menu-label">Closed on</span>
            {CLOSED_WINDOWS.map((window) => (
              <FilterMenuItem
                key={window.days}
                label={window.label}
                on={filter.closedWithinDays === window.days}
                kind="one"
                onClick={() => {
                  onChange({
                    ...filter,
                    closedWithinDays: filter.closedWithinDays === window.days ? null : window.days,
                  });
                }}
              />
            ))}
            {/* Leads closed before 20261219000100 have no date — nothing was
                backfilled, on purpose — so they match NO window. Saying so is
                the difference between a filter people trust and one they stop
                using; a silent omission is how a list stops being believed. */}
            {filter.closedWithinDays === null ? null : (
              <p className="lead-filter-menu-note">
                Leads closed before the Hub started recording closing dates have none, so they are
                not in this window. Clear it to see them.
              </p>
            )}
          </div>
        </FilterMenu>

        {/* Was "Only", which named the SQL rather than the thing. These are
            properties a lead carries, not conditions you compose. */}
        <FilterMenu {...menuProps('flags')}>
          <FilterMenuItem
            label="Awaiting first touch"
            on={filter.awaitingFirstTouchOnly}
            kind="many"
            onClick={() => {
              onChange({ ...filter, awaitingFirstTouchOnly: !filter.awaitingFirstTouchOnly });
            }}
          />
          <FilterMenuItem
            label="In nurture"
            on={filter.nurtureOnly}
            kind="many"
            onClick={() => {
              onChange({ ...filter, nurtureOnly: !filter.nurtureOnly });
            }}
          />
          <FilterMenuItem
            label="Bad contact"
            on={filter.badContactOnly}
            kind="many"
            onClick={() => {
              onChange({ ...filter, badContactOnly: !filter.badContactOnly });
            }}
          />
          {/* Combines with an origin filter, which is the point: a saved view
              cannot express "Inbound + LinkedIn Ads + nothing scheduled", and
              that combination is the list Alex actually asked for. */}
          <FilterMenuItem
            label="No next action"
            on={filter.noNextActionOnly}
            kind="many"
            onClick={() => {
              onChange({ ...filter, noNextActionOnly: !filter.noNextActionOnly });
            }}
          />
          {/* Not part of Needs Review, on purpose. Lead Origin was off every
              form from batch 49 until Drop 2c, so nearly every older lead lacks
              one — folding it into the tab would make the tab the whole
              database and bury the Lead Source gap inside it. */}
          <FilterMenuItem
            label="No Lead Origin"
            on={filter.noOriginOnly}
            kind="many"
            onClick={() => {
              onChange({ ...filter, noOriginOnly: !filter.noOriginOnly });
            }}
          />
          <FilterMenuItem
            label="No capture method"
            on={filter.noCaptureMethodOnly}
            kind="many"
            onClick={() => {
              onChange({ ...filter, noCaptureMethodOnly: !filter.noCaptureMethodOnly });
            }}
          />
          {/* 228 of 690 leads, measured. A third of the database with no
              company at all, and no way to see them as a group until now. */}
          <FilterMenuItem
            label="No company"
            on={filter.noCompanyOnly}
            kind="many"
            onClick={() => {
              onChange({ ...filter, noCompanyOnly: !filter.noCompanyOnly });
            }}
          />
          <FilterMenuItem
            label="No touch in 14 days"
            on={filter.staleAfterDays === 14}
            kind="many"
            onClick={() => {
              onChange({ ...filter, staleAfterDays: filter.staleAfterDays === 14 ? null : 14 });
            }}
          />
          {/* Was "Last 30 days", renamed in Drop 38 and the rename is the point
              rather than a tidy-up: the Closed menu offers a "Last 30 days" of
              its own, and two controls with the same words — one about
              arriving, one about ending — is a bar nobody can read. */}
          <FilterMenuItem
            label="Added in the last 30 days"
            on={filter.createdWithinDays === 30}
            kind="many"
            onClick={() => {
              onChange({
                ...filter,
                createdWithinDays: filter.createdWithinDays === 30 ? null : 30,
              });
            }}
          />
        </FilterMenu>

        {/* Neither of these is a filter, so they sit apart at the far end
            rather than becoming a seventh and eighth button of the same shape.
            Views SETS filters and Rounds REDRAWS the list; a row of eight
            identical buttons would say all eight do the same kind of thing. */}
        <span className="lead-filter-aside">
          {/* The four saved views, in one small control. Kyu: they were "so
              much bigger than the actual important major tabs... bad UI design
              right there. Put them in a dropdown." When a view is active the
              control wears its name, so "which list am I looking at" stays
              answered. */}
          <span className="lead-views-select">
            <Select
              label="Views"
              labelHidden
              value={currentView ?? ''}
              onChange={(event) => {
                const view = SAVED_VIEWS.find((entry) => entry.key === event.target.value);
                if (view === undefined) {
                  // Back to "Views": clear the view but KEEP the tab. Yanking
                  // somebody from Inbound to All Leads because they closed a
                  // view would make the control feel haunted.
                  onChange(withLeadTab(EMPTY_FILTER, currentTab));
                  return;
                }
                // No panel to open any more. What a view set shows up as a
                // count on the menus it touched and as tokens in the row below,
                // which is the same promise the open-on-load rule used to make.
                onChange(view.build(byName));
              }}
              options={[
                { value: '', label: 'Views' },
                ...SAVED_VIEWS.map((view) => ({
                  value: view.key,
                  label:
                    view.key === 'cg-recent-inbound' && view.build(byName).categoryIds.length === 0
                      ? `${view.label} ⚠`
                      : view.label,
                })),
              ]}
            />
          </span>

          {/* The follow-up work queue. A VIEW like Group by company — it
              redraws the current list grouped by round number — so it composes
              with the tab and every menu. Drawn only where the page can act
              on it (see the prop). */}
          {onToggleRounds === undefined ? null : (
            <button
              type="button"
              className={`lead-filter-more${roundsView === true ? ' lead-filter-more--active' : ''}`}
              aria-pressed={roundsView === true}
              title="Group the list by follow-up round, worst-off first. Shows only leads currently in a round."
              onClick={onToggleRounds}
            >
              Rounds
            </button>
          )}
        </span>
      </div>

      {/* What is narrowing the list, in one line, whether or not the control
          that set it is on screen. Absent entirely when nothing is set, so a
          clean list carries no chrome. */}
      <div className="lead-filter-active">
        {tokens.map((token) => (
          <button
            key={token.key}
            type="button"
            className="lead-filter-token"
            aria-label={`Remove filter: ${token.label}`}
            onClick={() => onChange(token.clear(filter))}
          >
            {token.label}
            <span aria-hidden="true">×</span>
          </button>
        ))}
        {!isFiltering(filter) ? null : (
          <Button variant="ghost" size="sm" onClick={() => onChange(EMPTY_FILTER)}>
            Clear filters
          </Button>
        )}
        <span className="lead-filter-count">
          {matchCount === totalCount ? (
            <>{totalCount} leads</>
          ) : (
            <>
              <Badge tone="brand">{matchCount}</Badge> of {totalCount} leads
            </>
          )}
        </span>
      </div>
    </div>
  );
}

function FilterGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="lead-filter-group">
      <span className="lead-filter-group-label">{label}</span>
      <div className="lead-filter-chips">{children}</div>
    </div>
  );
}

function Chip({
  on,
  onClick,
  title,
  children,
}: {
  on: boolean;
  onClick: () => void;
  /** Hover explanation. The loop chips need one; "Behind" means nothing on its own. */
  title?: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className={on ? 'lead-filter-chip lead-filter-chip--on' : 'lead-filter-chip'}
      aria-pressed={on}
      title={title}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
